import React from "react";
import ReactDOM, { Root } from "react-dom/client";

import { AppWrapper, ContainerProps } from "@/App";
import IFrameService from "@/service/IFrame";
import { inIframe } from "@/utils/getters";
import { standaloneRedirectUrl } from "@/utils/standaloneRedirect";
import { queryToJSON } from "@/utils/transform";

export default class ApplicationLoader {
  private readonly container: React.ComponentType<ContainerProps>;
  private readonly root: Root | null;
  private readonly query: IQuery;

  constructor(container: React.ComponentType<ContainerProps>) {
    this.container = container;
    this.unmount = this.unmount.bind(this);
    const searchParams = queryToJSON(window.location.search.slice(1));
    this.query = {
      id: searchParams.id,
      preview:
        searchParams.preview?.toLowerCase() === "true" ||
        searchParams.preview === "1",
      platform: searchParams.platform,
      locale: searchParams.locale,
      themeName: searchParams.theme,
    };

    // Standalone (a mobile-app webview opened this page top-level): hand off to the bild.de-origin
    // hyper wrapper before anything renders, so no widget flashes and the login cookie can be used
    // for the participation token. Embedded -- every web page, and the wrapper's own iframe -- this
    // is undefined and the page renders exactly as before, which is also what makes it loop-safe.
    const redirect = standaloneRedirectUrl(
      window.location.hostname,
      window.location.pathname,
      window.location.search,
      inIframe()
    );
    if (redirect) {
      window.location.replace(redirect);
      this.root = null;
      return;
    }

    const ROOT_NODE: HTMLElement | null = document.getElementById("root");

    if (!ROOT_NODE) {
      throw new Error("Could not find container element with id 'root'.");
    }
    this.root = ReactDOM.createRoot(ROOT_NODE);
  }

  public unmount(): void {
    if (!this.root) {
      return;
    }
    this.root.unmount();
    IFrameService.destroy();
  }

  public getInteractionId(): string {
    return this.query.id || "";
  }

  public start(): void {
    if (!this.root) {
      return;
    }
    this.root.render(
      <AppWrapper
        Container={this.container}
        onDestroy={this.unmount}
        query={this.query}
      />
    );
  }
}
